/**
 * Idempotency keys for `POST /v1/api/tasks/schedule`.
 *
 * WHAT THIS IS
 *   `key = ${fileUpload.id}:${attemptNonce}`, plus the registry that decides
 *   when the nonce is reused and when a fresh one is minted:
 *     - an AUTOMATIC retry (the transient-failure ladder) reuses the nonce, so
 *       professorx recognises the second call as the same logical schedule and
 *       replays the first outcome instead of creating a second workflow;
 *     - a USER-INITIATED Retry mints a new nonce, because the user is asking for
 *       a genuinely new attempt and must not be dedup'd against the failure.
 *
 * WHY IT EXISTS (evidence, 2026-09-05 Mongo write-stall incident)
 *   There is no `Idempotency-Key` anywhere on the client today
 *   (CORE-PIPELINE-REFERENCE §10). The schedule call's two blind retries are
 *   therefore three independent workflow creations, and during the stall 303 of
 *   11,882 upload uuids ended up with duplicate workflows in 24 h (2.5%; the
 *   plan's week-one target is < 0.1%).
 *
 *   Correction 18 settles the shape: do NOT invent a second UUID.
 *   `FileUpload.id` already IS the client-minted UUID and is already sent as the
 *   `id` form field, so the key is that id plus a per-attempt nonce. The server
 *   fallback `(uuid, toType, options-hash)` is unsafe on its own precisely
 *   because today's Retry re-sends an identical object and would dedupe against
 *   its own failure.
 *
 *   One key per FileUpload, not per input file: a merge of 3 files is one card,
 *   one schedule call and one workflow (see utils/file-upoad.utils.spec.ts).
 *
 *   The key must survive a JSON round-trip because it is persisted beside the
 *   card in localStorage — a reload mid-retry has to resume with the SAME key,
 *   or the resumed attempt creates the duplicate the key exists to prevent.
 *
 * PLAN SECTION
 *   MONGO-STALL-PLAN-v5.2.md §3 (item B), "Item B, restated", corrections 12,
 *   18; §7 metric 5 (duplicate workflows per upload uuid).
 *
 * WIRED 2026-09-05 behind `featureFlags.clientQueuedState` — see
 * `services/scheduling/schedule-coordinator.ts` (nonce ownership) and
 * `services/file-process.service.ts` (the `Idempotency-Key` request header).
 * The server-side claim store (`scope` + partial-unique index + 24 h TTL) is
 * separate work in item B (M4).
 */

/** Separator between the card id and the attempt nonce. */
export const IDEMPOTENCY_KEY_SEPARATOR = ':';

export interface IdempotencyKeyParts {
    fileUploadId: string;
    attemptNonce: string;
}

/** `${fileUpload.id}:${attemptNonce}`. */
export function buildIdempotencyKey(fileUploadId: string, attemptNonce: string): string {
    if (!fileUploadId) throw new Error('idempotency key needs a fileUpload id');
    if (!attemptNonce) throw new Error('idempotency key needs an attempt nonce');
    return `${fileUploadId}${IDEMPOTENCY_KEY_SEPARATOR}${attemptNonce}`;
}

/**
 * Split a key back into its parts. Only the FIRST separator is significant, so
 * a nonce may contain one; a FileUpload id (a UUID) never does.
 */
export function parseIdempotencyKey(key: string): IdempotencyKeyParts | null {
    if (typeof key !== 'string') return null;
    const at = key.indexOf(IDEMPOTENCY_KEY_SEPARATOR);
    if (at <= 0 || at === key.length - 1) return null;
    return {
        fileUploadId: key.slice(0, at),
        attemptNonce: key.slice(at + 1),
    };
}

/** The registry's persisted form — plain JSON, safe in localStorage. */
export interface IdempotencyRegistrySnapshot {
    version: 1;
    nonces: Record<string, string>;
}

export interface IdempotencyKeyRegistryOptions {
    /** Injectable nonce source, so tests are deterministic. */
    mintNonce?: () => string;
    /** Pre-seed from a persisted snapshot. */
    snapshot?: IdempotencyRegistrySnapshot | null;
}

/** Default nonce: short, collision-resistant enough, no crypto/DOM dependency. */
function defaultMintNonce(): string {
    const random = Math.random().toString(36).slice(2, 10);
    const time = Date.now().toString(36);
    return `${time}${random}`;
}

/**
 * Holds one attempt nonce per FileUpload id for the life of a batch, and knows
 * the difference between "try again" and "the user asked again".
 */
export class IdempotencyKeyRegistry {
    private readonly nonces = new Map<string, string>();
    private readonly mintNonce: () => string;

    constructor(options: IdempotencyKeyRegistryOptions = {}) {
        this.mintNonce = options.mintNonce ?? defaultMintNonce;
        const seeded = options.snapshot?.nonces;
        if (seeded) {
            Object.keys(seeded).forEach(id => this.nonces.set(id, seeded[id]));
        }
    }

    /**
     * The key for this card's CURRENT attempt. Mints a nonce the first time and
     * returns the same key thereafter — this is the automatic-retry path.
     */
    keyFor(fileUploadId: string): string {
        return buildIdempotencyKey(fileUploadId, this.nonceFor(fileUploadId));
    }

    /** The current nonce, minting one if this card has not been seen. */
    nonceFor(fileUploadId: string): string {
        if (!fileUploadId) throw new Error('idempotency key needs a fileUpload id');
        let nonce = this.nonces.get(fileUploadId);
        if (nonce === undefined) {
            nonce = this.mintNonce();
            this.nonces.set(fileUploadId, nonce);
        }
        return nonce;
    }

    /**
     * Seed the nonce a previous page load minted for this card, read back off
     * the persisted FileUpload. Wired 2026-09-05: a tab reloaded mid-retry MUST
     * resume with the same key, or the resumed attempt creates exactly the
     * duplicate workflow the key exists to prevent (correction 18). Never
     * overwrites a nonce this registry already holds — the live attempt wins.
     */
    adopt(fileUploadId: string, attemptNonce: string): string {
        if (!fileUploadId) throw new Error('idempotency key needs a fileUpload id');
        if (!attemptNonce) return this.nonceFor(fileUploadId);
        if (!this.nonces.has(fileUploadId)) {
            this.nonces.set(fileUploadId, attemptNonce);
        }
        return this.nonces.get(fileUploadId)!;
    }

    /**
     * A user-pressed Retry: mint a NEW nonce so the attempt is not dedup'd
     * against the stored outcome of the failed one. Returns the new key.
     */
    renewFor(fileUploadId: string): string {
        if (!fileUploadId) throw new Error('idempotency key needs a fileUpload id');
        this.nonces.set(fileUploadId, this.mintNonce());
        return this.keyFor(fileUploadId);
    }

    /** True once this card has a nonce (i.e. has been scheduled at least once). */
    has(fileUploadId: string): boolean {
        return this.nonces.has(fileUploadId);
    }

    /** Drop a card — it completed, or the user removed it. */
    forget(fileUploadId: string): void {
        this.nonces.delete(fileUploadId);
    }

    get size(): number {
        return this.nonces.size;
    }

    /** One key per tracked FileUpload. */
    allKeys(): string[] {
        return Array.from(this.nonces.keys()).map(id => this.keyFor(id));
    }

    toSnapshot(): IdempotencyRegistrySnapshot {
        const nonces: Record<string, string> = {};
        this.nonces.forEach((nonce, id) => { nonces[id] = nonce; });
        return { version: 1, nonces };
    }

    /** Lets `JSON.stringify(registry)` produce the snapshot directly. */
    toJSON(): IdempotencyRegistrySnapshot {
        return this.toSnapshot();
    }

    static fromSnapshot(
        snapshot: IdempotencyRegistrySnapshot | null | undefined,
        options: Omit<IdempotencyKeyRegistryOptions, 'snapshot'> = {},
    ): IdempotencyKeyRegistry {
        const usable = snapshot && snapshot.version === 1 && snapshot.nonces
            ? snapshot
            : null;
        return new IdempotencyKeyRegistry({ ...options, snapshot: usable });
    }
}

export function createIdempotencyKeyRegistry(
    options?: IdempotencyKeyRegistryOptions,
): IdempotencyKeyRegistry {
    return new IdempotencyKeyRegistry(options);
}
